// RouteLayer — render patrol routes on the floor command-view SVG.
//
// Plan B (grid A*) replaces the plan A pipeline (buildNavGraph +
// computePath + rectilinearisePolyline). Plan A relied on the parsed
// layout shipping room polygons and corridor centerlines; in production
// we hit floor plans that emit only walls + doors, which collapsed the
// nav graph and forced the rectilinearise step into fallback diagonals
// that cut straight through walls.
//
// Plan B rasterises the walls into a grid (doors carve free cells back
// out) and runs 4-connected A* between consecutive location markers.
// Output is orthogonal by construction (no diagonal moves) and obeys
// every wall except the ones with doors on them.
//
// Per-route visibility — same routeVisibility map as before. Missing
// keys / true = visible, false = hidden.

import React, { useMemo } from "react";
import type { CommandViewResponse } from "../../lib/floorStackApi.js";
import type { LayoutSubset } from "../../lib/navigation/buildGraph.js";
import { astarOrthogonal, rasteriseWalls, type Point, type RasterGrid } from "../../lib/navigation/gridAstar.js";
import { applyLaneOffsets } from "../../lib/navigation/laneOffset.js";

export const ROUTE_PALETTE = ["#ffa743", "#a78bfa", "#06b6d4", "#22c55e", "#f472b6"];

interface Props {
  data: CommandViewResponse;
  /** Master toggle for the whole layer. */
  visible: boolean;
  /** Per-route on/off. Missing keys / `true` = visible, `false` = hidden. */
  routeVisibility?: Record<string, boolean>;
}

interface PreparedRoute {
  id: string;
  name: string;
  colour: string;
  /** Final SVG polyline points (already orthogonal — comes from A* directly). */
  polyline: Point[];
  /** Sequence markers: one per location with a marker, in sequence order. */
  sequence: Array<{ location_id: string; sequence: number; x: number; y: number }>;
}

export default function RouteLayer({ data, visible, routeVisibility }: Props): React.ReactElement | null {
  // Parse the floor's normalized layout (string OR pre-parsed object).
  const layout = useMemo<LayoutSubset | null>(() => {
    const raw = data.layout?.current_version?.normalized_json;
    if (!raw) return null;
    try {
      return (typeof raw === "string" ? JSON.parse(raw) : raw) as LayoutSubset;
    } catch {
      return null;
    }
  }, [data.layout]);

  // Rasterise once per layout — every route on this floor shares the grid.
  const grid = useMemo<RasterGrid | null>(() => (layout ? rasteriseWalls(layout) : null), [layout]);

  const prepared = useMemo<PreparedRoute[]>(() => {
    if (!data.routes.length) return [];
    return data.routes.map((route, idx) => {
      const colour = ROUTE_PALETTE[idx % ROUTE_PALETTE.length];
      const cps = route.locations
        .filter((rc) => rc.marker)
        .sort((a, b) => a.sequence - b.sequence);
      const sequence = cps.map((rc) => ({
        location_id: rc.location_id,
        sequence: rc.sequence,
        x: rc.marker!.position_x,
        y: rc.marker!.position_y,
      }));
      if (sequence.length < 2) {
        return { id: route.id, name: route.name, colour, polyline: [], sequence };
      }
      // Build the polyline. With a rasterised grid we A* between every
      // consecutive marker pair; without one we fall back to straight
      // dot-to-dot. Concatenate per-segment polylines, dropping the
      // duplicate join points.
      let polyline: Point[] = [];
      if (grid) {
        polyline.push({ x: sequence[0].x, y: sequence[0].y });
        for (let i = 0; i < sequence.length - 1; i++) {
          const from: Point = { x: sequence[i].x, y: sequence[i].y };
          const to: Point = { x: sequence[i + 1].x, y: sequence[i + 1].y };
          const r = astarOrthogonal(grid, from, to);
          if (r.polyline.length < 2) {
            // No path found — degrade to straight line for this segment
            // so the visual still connects rather than disappearing.
            polyline.push(to);
            continue;
          }
          for (let k = 1; k < r.polyline.length; k++) polyline.push(r.polyline[k]);
        }
      } else {
        polyline = sequence.map((s) => ({ x: s.x, y: s.y }));
      }
      return { id: route.id, name: route.name, colour, polyline, sequence };
    });
  }, [data.routes, grid]);

  // Apply symmetric per-route lane offsets so two routes sharing a
  // corridor render as parallel-but-separated lines instead of a
  // single merged line. Memoised on the prepared routes — re-runs only
  // when the underlying polylines change. See laneOffset.ts header for
  // the algorithm + corner-stitching rationale.
  const preparedWithLanes = useMemo<PreparedRoute[]>(() => {
    const offsets = applyLaneOffsets(
      prepared.map((p) => ({ routeId: p.id, polyline: p.polyline })),
    );
    return prepared.map((p, i) => ({ ...p, polyline: offsets[i].polyline }));
  }, [prepared]);

  if (!visible) return null;
  const renderable = preparedWithLanes.filter((p) => p.polyline.length >= 2 && (routeVisibility?.[p.id] ?? true));
  if (renderable.length === 0) return null;

  return (
    <g data-layer="routes" pointerEvents="none">
      {renderable.map((r) => {
        // Build the SVG path "d" attribute once — we reuse it for both
        // the visible route line AND the <animateMotion> motion track
        // on the looping ball.
        const d = polylineToPathD(r.polyline);
        // Tie the loop duration to the route's total length so a short
        // route doesn't pulse too fast and a long one doesn't feel like
        // it's grinding. ~70 px/sec, clamped [3s, 10s] — feels lively
        // without becoming visually exhausting at a desk-glance distance.
        const totalPx = polylineLengthPx(r.polyline);
        const durSec = Math.max(3, Math.min(10, totalPx / 70));
        return (
          <g key={r.id}>
            <path
              d={d}
              fill="none"
              stroke={r.colour}
              strokeWidth={3}
              strokeOpacity={0.3}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {/* Pulse ball — travels start → end, then loops. animateMotion
                interpolates the circle's position along the same path "d"
                we draw the route with, so the ball stays exactly on the
                line at all times. Standard SMIL — works in every browser
                we ship to without any framer-motion / lottie cost. */}
            <circle r={5} fill={r.colour} opacity={0.95}>
              <animateMotion
                dur={`${durSec}s`}
                repeatCount="indefinite"
                path={d}
                rotate="auto"
              />
            </circle>
            {/* Sequence number badges at each location */}
            {r.sequence.map((s) => (
              <g key={`${r.id}-${s.location_id}`} transform={`translate(${s.x}, ${s.y})`}>
                <circle r={8} fill={r.colour} stroke="#0b0d10" strokeWidth={1} opacity={0.85} />
                <text x={0} y={3} fontSize={9} fontWeight={700} fill="#0b0d10" textAnchor="middle">{s.sequence}</text>
              </g>
            ))}
          </g>
        );
      })}
    </g>
  );
}

/** Turn a polyline into an SVG path "d" attribute — first point as
 *  Move, every other as Line. Single source of truth for the visible
 *  stroke AND the animateMotion path. */
function polylineToPathD(points: Point[]): string {
  if (points.length === 0) return "";
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) d += ` L ${points[i].x} ${points[i].y}`;
  return d;
}

/** Sum of segment lengths. Used to scale the pulse-ball loop duration
 *  so visual cadence stays consistent across short / long routes. */
function polylineLengthPx(points: Point[]): number {
  let t = 0;
  for (let i = 1; i < points.length; i++) {
    t += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  return t;
}
